import type { CheckTaskViewData, TaskViewData } from '@nova/core';

import {
  isAssetCheckTask,
  isInventoryCheckTask,
  isSpotAreaCheckTask,
  isSpotCheckTask
} from '@/helpers/checkTaskType';

/** Narrowed to the shape this helper reads, mirroring `checkTaskType.ts`. */
type CancellableTask =
  | (Pick<TaskViewData, 'type'> & Partial<Pick<CheckTaskViewData, 'relatedEntity'>>)
  | null
  | undefined;

/** `useI18n().t`, narrowed to the plain key lookup used here. */
type Translate = (key: string) => string;

const MOVE_REASON_KEYS = [
  'yard.messages.cancelTask.move.reasons.noLongerNecessary',
  'yard.messages.cancelTask.move.reasons.assetUnavailable',
  'yard.messages.cancelTask.move.reasons.destinationUnavailable'
] as const;

const ASSET_CHECK_REASON_KEYS = [
  'yard.messages.cancelTask.assetCheck.reasons.noLongerNecessary',
  'yard.messages.cancelTask.assetCheck.reasons.assetInaccessible',
  'yard.messages.cancelTask.assetCheck.reasons.equipmentUnavailable'
] as const;

const SPOT_CHECK_REASON_KEYS = [
  'yard.messages.cancelTask.spotCheck.reasons.noLongerNecessary',
  'yard.messages.cancelTask.spotCheck.reasons.spotUnavailable',
  'yard.messages.cancelTask.spotCheck.reasons.surfaceHazard'
] as const;

const AREA_CHECK_REASON_KEYS = [
  'yard.messages.cancelTask.areaCheck.reasons.noLongerNecessary',
  'yard.messages.cancelTask.areaCheck.reasons.restrictedAccess',
  'yard.messages.cancelTask.areaCheck.reasons.equipmentUnavailable'
] as const;

const INVENTORY_CHECK_REASON_KEYS = [
  'yard.messages.cancelTask.inventoryCheck.reasons.noLongerNecessary',
  'yard.messages.cancelTask.inventoryCheck.reasons.reassignedToAnotherShift',
  'yard.messages.cancelTask.inventoryCheck.reasons.inventoryInaccessible'
] as const;

export type CancelTaskDrawerContent = {
  subtitle?: string;
  reasonOptions?: string[];
};

/**
 * Drawer content for `task`: the sentence spelling out the consequence, and the
 * pre-filled reasons offered above "Other...".
 *
 * Check tasks are discriminated by `relatedEntity` (see `checkTaskType.ts`), never by `category`.
 * The asset, spot, area and inventory flavours each have dedicated content; anything unrecognized
 * returns no content and relies on the drawer's always-present "Other..." free-text option.
 */
export function getCancelTaskDrawerContent(
  task: CancellableTask,
  t: Translate
): CancelTaskDrawerContent {
  if (task?.type === 'move') {
    return {
      subtitle: t('yard.messages.cancelTask.move.subtitle'),
      reasonOptions: MOVE_REASON_KEYS.map(key => t(key))
    };
  }

  if (isAssetCheckTask(task)) {
    return {
      subtitle: t('yard.messages.cancelTask.assetCheck.subtitle'),
      reasonOptions: ASSET_CHECK_REASON_KEYS.map(key => t(key))
    };
  }

  if (isSpotCheckTask(task)) {
    return {
      subtitle: t('yard.messages.cancelTask.spotCheck.subtitle'),
      reasonOptions: SPOT_CHECK_REASON_KEYS.map(key => t(key))
    };
  }

  if (isSpotAreaCheckTask(task)) {
    return {
      subtitle: t('yard.messages.cancelTask.areaCheck.subtitle'),
      reasonOptions: AREA_CHECK_REASON_KEYS.map(key => t(key))
    };
  }

  if (isInventoryCheckTask(task)) {
    return {
      subtitle: t('yard.messages.cancelTask.inventoryCheck.subtitle'),
      reasonOptions: INVENTORY_CHECK_REASON_KEYS.map(key => t(key))
    };
  }

  return {};
}
